import { canAssign } from "./kindMeta";

// Action registry for the Now feed.
//
// THE "DON'T HARDCODE THE KINDS" CONTRACT. The set of kinds is `ops_signals` rows and
// grows server-side, so the LIST is data-driven (NowPage iterates whatever `items[]`
// returns). This map holds ONLY the kind-specific EXTRA affordances (spec §4). The
// row's own `dismissable` + `dismiss_label` are rendered generically by NowRow and are
// deliberately NOT in this map.
//
// ROUTING (backend response 2026-09-07): affordances follow the row's `subject_type`,
// not its `kind`. `subject_type` says which table the id targets — commitment · thread ·
// task · message — so a kind added later works with no code change here. A kind IS listed
// below only where its affordance is more specific than the subject_type default: the
// three `task` kinds each take a different write (open vs chase vs move), and
// payment_failed is a billing wall whose only affordance is a link to the client.
//
// SAFE DEFAULT: a kind matched by neither map yields `[]`, so NowRow renders only the
// row-provided dismiss control. The screenshot proves this on `renewal_upcoming`.
//
// Each affordance: { key, label, action, variant?, to?, title?, when?, demotedBy? }. `title`
// is the hover text and accessible description where the label alone is terse. `when(item)`
// hides the affordance for rows it does not fit. `demotedBy` names a sibling affordance
// key: while that sibling is shown, this one is a secondary rather than the primary. `action` is a handler NAME that
// NowPage wires to a function; NowRow handles `"composer"` locally (opens the inline
// Composer). An affordance with `to(item)` is a plain link (no write): NowRow renders it
// only when `to` returns a path.
// `markPromiseMet` maps to the commitments RPC directly — never `dismiss`.

const MET = { key: "met", label: "Done", action: "markPromiseMet", variant: "primary" };
const REPLY = { key: "reply", label: "Reply", action: "composer", variant: "primary" };
const RELEASE = { key: "release", label: "Release", action: "releaseAgent", variant: "primary" };

// Kind-specific overrides — only where the affordance is narrower than the subject_type
// default (task disambiguation) or deliberately absent (payment_failed).
export const KIND_ACTIONS = {
  // task — deliver the request the client asked for.
  request_unanswered: [
    { key: "open", label: "Open", action: "openRequest", variant: "primary" },
  ],
  // task — take the work (assign it to the signed-in user) or move it (fn_work_move needs
  // a target status; see now.js). Take is the primary while the task is still unowned and
  // Move work is then the secondary; once owned, Move work is the primary.
  needs_assistance: [
    { key: "take", label: "Take", action: "take", variant: "primary", when: canAssign },
    {
      key: "move",
      label: "Move work",
      action: "moveWork",
      variant: "primary",
      demotedBy: "take",
    },
  ],
  // task — chasing IS the action; touches the task and restarts the 30-day clock.
  chase_client: [
    { key: "chased", label: "Chased", action: "markChased", variant: "primary" },
  ],
  // message — Release approves; Edit returns to draft (dismiss_label "Back to draft").
  agent_waiting: [
    RELEASE,
    // Edit returns the draft to its author (fn_agent_edit), so the hover text says what it
    // does. The label is the mockup's; the accessible name keeps that word in it.
    {
      key: "edit",
      label: "Edit",
      action: "editAgent",
      title: "Back to draft",
      ariaLabel: "Edit (back to draft)",
    },
  ],
  // billing.read wall — no write exists to chase an invoice, so the row links to the
  // client. Rendered only when the row carries a client_id.
  payment_failed: [
    {
      key: "view",
      label: "View",
      action: "navigate",
      variant: "primary",
      to: (item) =>
        item?.client_id ? `/hub/clients/${encodeURIComponent(item.client_id)}` : null,
    },
  ],
};

// Default affordance per subject_type, for any kind not overridden above. This is what
// gives the SLA/thread kinds (sla_first_response, sla_no_commitment, sla_gone_quiet, …)
// a Reply with no per-kind entry, and makes a future kind work by its subject_type alone.
// `task` has no generic default — its action is kind-specific (see KIND_ACTIONS).
const SUBJECT_TYPE_ACTIONS = {
  commitment: [MET],
  thread: [REPLY],
  message: [RELEASE],
};

/**
 * The extra affordances for a row: its kind-specific override if any, else the default
 * for its subject_type, else `[]` (safe default → only the row's dismiss control).
 * @param {{kind?: string, subject_type?: string}} item  One fn_now item.
 * @returns {{key: string, label: string, action: string, variant?: string}[]}
 */
export const affordancesFor = (item) => {
  if (!item) return [];
  const list = KIND_ACTIONS[item.kind] || SUBJECT_TYPE_ACTIONS[item.subject_type] || [];
  const shown = list.filter((a) => !a.when || a.when(item));
  return shown.map((a) =>
    a.demotedBy && shown.some((o) => o.key === a.demotedBy)
      ? { ...a, variant: "secondary" }
      : a,
  );
};
